/**
 * CGM-FIELD-COMMISSIONING-R2 — read the canonical field bindings once.
 *
 * The parcel → valve-channel and pump → relay bindings are backend-authoritative
 * (the manifest). The frontend consumes them read-only: it never stores or
 * invents a channel id, and it fails soft (empty) so a bindings read failure
 * degrades every parcel/pump surface to "not commissioned" rather than guessing.
 */

import { createContext, createElement, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { iotApi, type FieldBindings, type ParcelValveBinding, type PumpBindingView } from '../lib/iotApi';

export interface FieldBindingsState {
  farmId?: string;
  pressureSensorByBinding?: Record<string, string>;
  parcelBindings: ParcelValveBinding[];
  pumps: PumpBindingView[];
  loaded: boolean;
  error: boolean;
}

const FieldBindingsContext = createContext<FieldBindingsState | null>(null);

export function FieldBindingsProvider({ children }: { children: ReactNode }) {
  const value = useFieldBindings();
  return createElement(FieldBindingsContext.Provider, { value }, children);
}

export function useFieldBindings(): FieldBindingsState {
  const shared = useContext(FieldBindingsContext);
  const hasShared = shared !== null;
  const [farmId, setFarmId] = useState<string>();
  const [pressureSensorByBinding, setPressureSensorByBinding] = useState<Record<string, string>>({});
  const [parcelBindings, setParcelBindings] = useState<ParcelValveBinding[]>([]);
  const [pumps, setPumps] = useState<PumpBindingView[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (hasShared) return;
    mountedRef.current = true;
    iotApi
      .getJson<FieldBindings>('/iot/field-bindings')
      .then((data) => {
        if (!mountedRef.current) return;
        setFarmId(data?.farm_id);
        setPressureSensorByBinding(data?.pressure_sensor_by_binding ?? {});
        setParcelBindings(Array.isArray(data?.parcel_valve_bindings) ? data.parcel_valve_bindings : []);
        setPumps(Array.isArray(data?.pumps) ? data.pumps : []);
        setLoaded(true);
        setError(false);
      })
      .catch(() => {
        // Fail soft: no binding is safer than a guessed binding.
        if (!mountedRef.current) return;
        setParcelBindings([]);
        setFarmId(undefined);
        setPressureSensorByBinding({});
        setPumps([]);
        setLoaded(true);
        setError(true);
      });
    return () => {
      mountedRef.current = false;
    };
  }, [hasShared]);

  return shared ?? { parcelBindings, pumps, loaded, error, farmId, pressureSensorByBinding };
}
